/* Linked variant picker + Essential Preorder ("Notify me") widget refresh.
   Extracted from global.js so Dawn "Update from Shopify" syncs never clobber
   this custom code. Loaded once via layout/theme.liquid (outside the swapped
   product section); the customElements.get guard is belt-and-suspenders in case
   a section-swap script re-run ever reaches it, which would otherwise throw
   "already defined". */
if (!customElements.get('linked-variant-picker-multi')) {

/* --- Stepwise ("guided funnel") mode --------------------------------------
   Every outer/inner/fur combination is a separate product, so brute-forcing the
   three swatch rows was the only way to find a combination that isn't sold out.
   Stepwise mode turns the rows into a cascade: Aussenfarbe -> Innenfarbe ->
   Fellfarbe -> Groesse. A step only offers the colours that still lead to an
   IN-STOCK product given the steps above it (everything else is greyed out and
   unselectable), and a step stays hidden until every step above it is confirmed.

   The cascade is computed from the productMapping emitted by
   snippets/linked-variant-picker-multi.liquid, which for this reason carries the
   dimensions explicitly (fur/inner/outer) and includes the current product.
   Opt in per block via the `stepwise_mode` setting -> data-stepwise="true". */

// Display order of the funnel. NOTE: this is NOT the mapping-key order, which is
// fur-inner-outer (see getCurrentProductKey).
const STEP_ORDER = ['outer', 'inner', 'fur'];
// data-category on the radios -> step name.
const STEP_BY_CATEGORY = { outside: 'outer', inside: 'inner', fur: 'fur' };
// Query param that asks the funnel to start from scratch instead of adopting the
// combination the page renders. The landing page appends it to its product links
// (snippets/product-grid-card.liquid) — keep both sides in sync.
const FRESH_PARAM = 'configure';

function applySizeAvailability(scope) {
    // Marks size options that are sold out for the product currently on screen.
    // Dawn only disables the buy button *after* a sold-out size is picked, which
    // is the same brute-forcing problem one level down — so grey them out up
    // front, matching how the colour steps behave.
    if (!scope) return;
    const container = scope.querySelector('variant-radios');
    if (!container) return;
    const dataScript = container.querySelector('script[type="application/json"]');
    if (!dataScript) return;
    let variants;
    try { variants = JSON.parse(dataScript.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    // A Weekender also needs its matching inlay in stock for the chosen size —
    // that is a SEPARATE stock pool, gated by applyInlayGate below. Without this
    // the row would offer a size the gate then refuses, which reads as the page
    // contradicting itself ("Größe XS selectable" + "Kombination nicht
    // verfügbar"). Fail OPEN like the gate: only a positively-known sold-out
    // inlay counts against a size.
    let inlayMap = null;
    const inlayData = scope.querySelector('inlay-stock-gate script[type="application/json"]');
    if (inlayData) {
        try { inlayMap = JSON.parse(inlayData.textContent); } catch (e) { inlayMap = null; }
    }

    const fieldsets = Array.from(container.querySelectorAll('fieldset'));
    const selected = fieldsets.map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
    });

    fieldsets.forEach((fieldset, idx) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const ok = variants.some((variant) => {
                if (!variant.available) return false;
                if (inlayMap && inlayMap[String(variant.id)] === false) return false;
                if (String(variant.options[idx]) !== input.value) return false;
                // Respect the other options the customer already picked, so a
                // multi-option product greys out the right combinations.
                return selected.every((value, j) => j === idx || value == null || String(variant.options[j]) === value);
            });
            // Never disable the customer's own current pick — it would silently
            // vanish from the row instead of reading as "sold out".
            const keep = input.checked;
            input.disabled = !ok && !keep;
            input.classList.toggle('is-unavailable', !ok);
            const label = input.nextElementSibling;
            if (label && label.tagName === 'LABEL') label.classList.toggle('is-unavailable', !ok);
        });
    });
}
class LinkedVariantPickerMulti extends HTMLElement {
    constructor() {
        super();
        // Add both change and click listeners for better coverage
        this.addEventListener('change', this.onVariantChange.bind(this));
        this.addEventListener('click', this.onVariantClick.bind(this));
        this.sectionId = this.dataset.section;
        this.currentInsideColor = this.dataset.currentInsideColor || null;
        this.currentOutsideColor = this.dataset.currentOutsideColor || null;
        this.currentFurColor = this.dataset.currentFurColor || null;
        this.currentProductHandle = this.dataset.currentProductHandle;
        console.log('Current product colors:', {
            inside: this.currentInsideColor,
            outside: this.currentOutsideColor,
            fur: this.currentFurColor,
            handle: this.currentProductHandle
        });
        this.productMapping = this.getProductMapping();
        this.initStepwise();
        this.init();
    }

    init() {
        console.log('LinkedVariantPickerMulti initialized');
        console.log('Section ID:', this.sectionId);
        console.log('Product mapping available:', !!this.productMapping);

        if (this.productMapping) {
            console.log('Product mapping keys:', Object.keys(this.productMapping));
            // Do NOT auto-swap on load: the server already rendered the correct
            // product. Swapping here risks an infinite cascade when a product's
            // own preselected combination resolves to a *different* product
            // (duplicated products, colliding keys, or inconsistent metafields).
            // Swaps happen only in response to user interaction (onVariantChange).
            this.prefetchLinkedProducts();
        } else {
            console.log('No product mapping - picker is in display-only mode');
        }
    }

    prefetchLinkedProducts() {
        const currentKey = this.getCurrentProductKey();
        this.prefetchCache = {};
        for (const [key, product] of Object.entries(this.productMapping)) {
            if (key !== currentKey && product.url) {
                this.prefetchCache[key] = fetch(product.url)
                    .then(r => r.text())
                    .then(html => {
                        this.preloadFirstImage(html);
                        return html;
                    })
                    .catch(e => { console.error('Prefetch failed for', key, e); return null; });
            }
        }
    }

    preloadFirstImage(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const firstImg = doc.querySelector('.product__media-list img[srcset]');
        if (!firstImg) return;
        const srcset = firstImg.getAttribute('srcset');
        const sources = srcset.split(',').map(s => s.trim()).filter(Boolean);
        const largest = sources[sources.length - 1];
        const url = largest ? largest.split(' ')[0] : null;
        if (url) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = url;
            document.head.appendChild(link);
        }
    }

    onVariantChange(event) {
        console.log('LinkedVariantPickerMulti - Change event detected:', {
            target: event.target,
            type: event.target.type,
            name: event.target.name,
            value: event.target.value,
            checked: event.target.checked
        });

        if (event.target.type === 'radio') {
            console.log('LinkedVariantPickerMulti - Radio button changed, updating product...');
            if (this.stepwise) {
                this.handleStepChange(event.target);
                return;
            }
            this.updateProduct();
        } else {
            console.log('LinkedVariantPickerMulti - Non-radio change event ignored');
        }
    }

    onVariantClick(event) {
        // Stepwise mode drives everything off the `change` event — a label click
        // that actually selects a radio always fires one, and this path would
        // otherwise also fire for clicks on greyed-out (disabled) swatches.
        if (this.stepwise) return;

        console.log('LinkedVariantPickerMulti - Click event detected:', {
            target: event.target,
            tagName: event.target.tagName,
            className: event.target.className,
            htmlFor: event.target.htmlFor
        });

        // If clicking on a label, find the associated radio button
        if (event.target.tagName === 'LABEL' && event.target.htmlFor) {
            const radio = document.getElementById(event.target.htmlFor);
            if (radio && radio.type === 'radio') {
                console.log('LinkedVariantPickerMulti - Label clicked, triggering radio change');
                // Let the browser handle the radio button selection naturally
                setTimeout(() => this.updateProduct(), 10);
            }
        }
    }

    /* --- Stepwise mode ---------------------------------------------------- */

    initStepwise() {
        this.stepwise = this.dataset.stepwise === 'true';
        if (!this.stepwise) return;

        // Only the dimensions this product actually renders take part in the funnel.
        this.steps = STEP_ORDER.filter((step) => this.stepCategory(step) !== null);
        this.combos = Object.entries(this.productMapping || {}).map(([key, entry]) =>
            Object.assign({ __key: key }, entry)
        );
        // Colours of the product currently on screen — used to pick the least
        // surprising product to swap to while the funnel is still incomplete.
        this.currentValues = {
            outer: this.currentOutsideColor ? this.normalizeKeyPart(this.currentOutsideColor) : null,
            inner: this.currentInsideColor ? this.normalizeKeyPart(this.currentInsideColor) : null,
            fur: this.currentFurColor ? this.normalizeKeyPart(this.currentFurColor) : null,
        };

        this.stepValues = {};
        for (const step of this.steps) {
            const checked = this.checkedInputFor(step);
            if (checked) this.stepValues[step] = this.normalizeKeyPart(checked.value);
        }

        // A product page always renders ONE concrete combination, so by default
        // every step counts as confirmed (deep links, collection clicks and the
        // back button land on a fully configured bag). Two things override that:
        // a ?configure link, which enters the funnel with nothing chosen, and a
        // swap, which carries a partially confirmed funnel over — see
        // swapProductSection().
        let confirmed = this.startsFresh() ? [] : this.steps.filter((step) => this.stepValues[step] != null);
        if (this.dataset.pendingStepState) {
            try {
                const pending = JSON.parse(this.dataset.pendingStepState);
                if (pending && Array.isArray(pending.confirmed)) {
                    confirmed = this.steps.filter((step) => pending.confirmed.indexOf(step) !== -1);
                }
            } catch (e) { /* fall back to "everything confirmed" */ }
            this.removeAttribute('data-pending-step-state');
        }
        this.confirmed = new Set(confirmed);

        // Steps the customer has not (re-)confirmed must not look answered, even
        // though the server rendered this product with a value for them.
        for (const step of this.steps) {
            if (this.confirmed.has(step)) continue;
            delete this.stepValues[step];
            this.inputsFor(step).forEach((input) => { input.checked = false; });
        }

        this.refreshStepUI();
    }

    // True when the page was entered through a "configure from scratch" link.
    startsFresh() {
        try {
            return new URLSearchParams(window.location.search).has(FRESH_PARAM);
        } catch (e) {
            return false;
        }
    }

    // Drop the marker once the customer has answered something, so a reload (or a
    // shared URL) does not throw away the progress they just made.
    clearFreshParam() {
        try {
            const url = new URL(window.location.href);
            if (!url.searchParams.has(FRESH_PARAM)) return;
            url.searchParams.delete(FRESH_PARAM);
            history.replaceState(history.state, '', url.toString());
        } catch (e) { /* non-fatal */ }
    }

    stepCategory(step) {
        const category = { outer: 'outside', inner: 'inside', fur: 'fur' }[step];
        return this.querySelector(`input[name="linked-variant-${category}-${this.sectionId}"]`) ? category : null;
    }

    inputsFor(step) {
        const category = { outer: 'outside', inner: 'inside', fur: 'fur' }[step];
        return Array.from(this.querySelectorAll(`input[name="linked-variant-${category}-${this.sectionId}"]`));
    }

    checkedInputFor(step) {
        const category = { outer: 'outside', inner: 'inside', fur: 'fur' }[step];
        return this.querySelector(`input[name="linked-variant-${category}-${this.sectionId}"]:checked`);
    }

    // Can this combination actually be bought in any size? `reachable` folds in
    // the matching inlay's stock (a separate pool), so a colour whose every size
    // is blocked by a missing inlay is treated as sold out rather than offered.
    // Falls back to plain availability for a mapping rendered before that field.
    isReachable(combo) {
        return combo.reachable == null ? !!combo.available : !!combo.reachable;
    }

    // Values still reachable for `step`, given the confirmed steps ABOVE it.
    // A value counts only if at least one matching product can actually be bought.
    availableValuesFor(step) {
        const upstream = {};
        for (const s of this.steps) {
            if (s === step) break;
            if (this.confirmed.has(s)) upstream[s] = this.stepValues[s];
        }
        const values = new Set();
        for (const combo of this.combos) {
            if (!this.isReachable(combo)) continue;
            let matches = true;
            for (const key in upstream) {
                if (combo[key] !== upstream[key]) { matches = false; break; }
            }
            if (matches && combo[step]) values.add(combo[step]);
        }
        return values;
    }

    handleStepChange(input) {
        const step = STEP_BY_CATEGORY[input.dataset.category];
        if (!step || this.steps.indexOf(step) === -1) return;

        this.stepValues[step] = this.normalizeKeyPart(input.value);
        this.confirmed.add(step);
        this.clearFreshParam();
        this.invalidateDownstream(step);

        const target = this.resolveStepTarget();
        if (target && target.handle && target.handle !== this.currentProductHandle) {
            const preserved = this.captureCurrentVariantOptions();
            // Reflect the new funnel state immediately; the swap only catches up
            // once the fetch resolves.
            this.refreshStepUI();
            this.swapProductSection(target, target.__key, preserved);
            return;
        }

        this.refreshStepUI();
    }

    // A colour picked further up can invalidate what was chosen below it. Drop
    // that selection (and everything after it) so the customer re-picks from the
    // colours that are actually still in stock, instead of landing on a dead end.
    invalidateDownstream(step) {
        const from = this.steps.indexOf(step) + 1;
        for (let i = from; i < this.steps.length; i++) {
            const below = this.steps[i];
            if (!this.confirmed.has(below)) break;
            if (this.availableValuesFor(below).has(this.stepValues[below])) continue;
            for (let j = i; j < this.steps.length; j++) {
                this.confirmed.delete(this.steps[j]);
                delete this.stepValues[this.steps[j]];
            }
            break;
        }
    }

    // The product to show for a partially configured funnel: it must match every
    // confirmed step, should be in stock, and among those we prefer the one that
    // differs least from what is on screen right now.
    resolveStepTarget() {
        const selection = {};
        for (const step of this.steps) {
            if (this.confirmed.has(step)) selection[step] = this.stepValues[step];
        }

        let candidates = this.combos.filter((combo) =>
            Object.keys(selection).every((step) => combo[step] === selection[step])
        );
        const inStock = candidates.filter((combo) => this.isReachable(combo));
        if (inStock.length) candidates = inStock;
        if (!candidates.length) return null;

        const score = (combo) => this.steps.reduce((total, step) => {
            if (step in selection) return total;
            const current = this.currentValues[step];
            return current && combo[step] === current ? total + 1 : total;
        }, 0);

        let best = candidates[0];
        let bestScore = score(best);
        for (const combo of candidates) {
            const value = score(combo);
            if (value > bestScore) { best = combo; bestScore = value; }
        }
        return best;
    }

    refreshStepUI() {
        if (!this.stepwise) return;

        // First drop the radios of every unconfirmed step: the server renders a
        // checked one for each dimension, and a stale check would both resurface
        // as an answer and shield that option from the sold-out treatment below.
        for (const step of this.steps) {
            if (this.confirmed.has(step)) continue;
            this.inputsFor(step).forEach((input) => { input.checked = false; });
        }

        let locked = false;
        for (const step of this.steps) {
            const category = this.querySelector(`.linked-variant-category[data-step="${step}"]`);
            if (category) {
                category.classList.toggle('is-locked', locked);
                const available = locked ? new Set() : this.availableValuesFor(step);
                category.querySelectorAll('.linked-variant-option').forEach((option) => {
                    const input = option.querySelector('input[type="radio"]');
                    const reachable = available.has(option.dataset.colorKey);
                    // Keep the customer's own pick selectable even when it is sold
                    // out, so it reads as "sold out" rather than disappearing.
                    const keep = !!(input && input.checked);
                    option.classList.toggle('is-unavailable', !reachable && !keep);
                    if (input) input.disabled = locked || (!reachable && !keep);
                });
            }
            if (!this.confirmed.has(step)) locked = true;
        }

        this.updateStepGate(!locked);
    }

    // `complete` = every colour step confirmed, so the exact product is resolved
    // and the size step can be trusted.
    updateStepGate(complete) {
        const hint = this.querySelector('[data-step-hint]');
        if (hint) {
            const next = this.steps.find((step) => !this.confirmed.has(step));
            const category = next ? this.querySelector(`.linked-variant-category[data-step="${next}"]`) : null;
            const label = category ? category.dataset.stepLabel : '';
            const template = this.dataset.hintTemplate || '';
            hint.textContent = next && template && label ? template.replace('%s', label) : '';
            hint.hidden = !hint.textContent;
        }

        const scope = this.closest('[id^="MainProduct-"]') || this.closest('.shopify-section');
        if (!scope) return;
        scope.classList.toggle('stepwise-incomplete', !complete);
        scope.querySelectorAll('variant-radios, variant-selects').forEach((picker) => {
            picker.classList.toggle('stepwise-hidden', !complete);
        });
        if (complete) applySizeAvailability(scope);
    }

    normalizeKeyPart(value) {
        if (!value) return value;
        const v = String(value).toLowerCase().replace(/[ -]/g, '_');
        // "taupe" is an accepted alias for the canonical "dark_taupe" key.
        return v === 'taupe' ? 'dark_taupe' : v;
    }

    getCurrentProductKey() {
        if (!this.productMapping) return null;
        const parts = [];
        if (this.currentFurColor) parts.push(this.normalizeKeyPart(this.currentFurColor));
        if (this.currentInsideColor) parts.push(this.normalizeKeyPart(this.currentInsideColor));
        if (this.currentOutsideColor) parts.push(this.normalizeKeyPart(this.currentOutsideColor));
        if (parts.length === 0) return null;
        return parts.join('-');
    }

    getProductMapping() {
        const script = this.querySelector(`#variant-mapping-${this.sectionId}`);
        if (script) {
            try {
                const data = JSON.parse(script.textContent);
                return data.productMapping;
            } catch (error) {
                console.error('Error parsing productMapping', error);
                return {};
            }
        }
        return {};
    }

    getSelectedCombination() {
        const insideInput = this.querySelector(`input[name="linked-variant-inside-${this.sectionId}"]:checked`);
        const outsideInput = this.querySelector(`input[name="linked-variant-outside-${this.sectionId}"]:checked`);
        const furInput = this.querySelector(`input[name="linked-variant-fur-${this.sectionId}"]:checked`);

        const insideFieldExists = !!this.querySelector(`input[name="linked-variant-inside-${this.sectionId}"]`);
        const outsideFieldExists = !!this.querySelector(`input[name="linked-variant-outside-${this.sectionId}"]`);
        const furFieldExists = !!this.querySelector(`input[name="linked-variant-fur-${this.sectionId}"]`);

        console.log('LinkedVariantPickerMulti - Checking selected inputs:', {
            insideInput: insideInput ? { name: insideInput.name, value: insideInput.value } : null,
            outsideInput: outsideInput ? { name: outsideInput.name, value: outsideInput.value } : null,
            furInput: furInput ? { name: furInput.name, value: furInput.value } : null,
            insideFieldExists, outsideFieldExists, furFieldExists,
            sectionId: this.sectionId
        });

        // A dimension that's rendered in the DOM must have a selection.
        if (insideFieldExists && !insideInput) { console.log('LinkedVariantPickerMulti - inside dimension present but unselected'); return null; }
        if (outsideFieldExists && !outsideInput) { console.log('LinkedVariantPickerMulti - outside dimension present but unselected'); return null; }
        if (furFieldExists && !furInput) { console.log('LinkedVariantPickerMulti - fur dimension present but unselected'); return null; }

        const parts = [];
        if (furInput) parts.push(this.normalizeKeyPart(furInput.value));
        if (insideInput) parts.push(this.normalizeKeyPart(insideInput.value));
        if (outsideInput) parts.push(this.normalizeKeyPart(outsideInput.value));

        if (parts.length === 0) {
            console.log('LinkedVariantPickerMulti - no dimensions present at all');
            return null;
        }

        const combination = {
            inside: insideInput ? insideInput.value : null,
            outside: outsideInput ? outsideInput.value : null,
            fur: furInput ? furInput.value : null,
            key: parts.join('-'),
        };

        console.log('LinkedVariantPickerMulti - Generated combination:', combination);
        return combination;
    }

    updateProduct() {
        const combination = this.getSelectedCombination();

        console.log('Linked Variant Picker - Selected combination:', combination);
        console.log('Linked Variant Picker - Product mapping:', this.productMapping);

        if (!combination || !this.productMapping) {
            console.log('Linked Variant Picker - No combination or mapping available');
            return;
        }

        const product = this.productMapping[combination.key];

        if (product) {
            console.log('Linked Variant Picker - Found product:', product);
            this.handleProductFound(product);
        } else {
            console.log('Linked Variant Picker - No product found for combination:', combination.key);
            this.handleProductNotFound(combination);
        }
    }

    handleProductFound(product) {
        // Never swap to the product we're already on. Compare by handle — robust
        // to key-normalization mismatches — in addition to the combination key.
        if (product.handle && product.handle === this.currentProductHandle) {
            return;
        }
        const selectedKey = this.getSelectedCombination().key;
        if (this.getCurrentProductKey() == selectedKey) {
            return;
        }

        const preservedOptions = this.captureCurrentVariantOptions();
        this.swapProductSection(product, selectedKey, preservedOptions);

        this.dispatchEvent(new CustomEvent('variantChanged', {
            detail: { product, available: true }
        }));
    }

    captureCurrentVariantOptions() {
        // Reads Shopify variant option selections (size, material, etc.) from the
        // active product form — explicitly excluding linked-variant-* radios so we
        // don't try to mirror color picks across products.
        const captured = {};
        const container = document.querySelector('variant-radios, variant-selects');
        if (container) {
            container.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
                if (input.name && !input.name.startsWith('linked-variant-')) {
                    captured[input.name] = input.value;
                }
            });
            container.querySelectorAll('select').forEach((select) => {
                const m = select.name && select.name.match(/^options\[(.+)\]$/);
                if (m) captured[m[1]] = select.value;
            });
        }
        // The inlay configurator (mode + fur/outer colors) is independent of the
        // bag product, so its selection survives the section swap too.
        const inlayConfigurator = document.querySelector('inlay-configurator');
        if (inlayConfigurator && typeof inlayConfigurator.getState === 'function') {
            captured.__inlayState = inlayConfigurator.getState();
        }
        // Which funnel steps the customer has actually confirmed. Without this the
        // swapped-in section — which always renders one complete combination —
        // would show every step as answered again and collapse the funnel.
        if (this.stepwise) {
            captured.__stepState = { confirmed: Array.from(this.confirmed) };
        }
        return captured;
    }

    applyPreservedOptions(section, captured) {
        if (!section || !captured) return;
        const inlayState = captured.__inlayState;
        delete captured.__inlayState;
        // Consumed in swapProductSection (before the section goes live) so the new
        // picker can pick it up in its constructor; drop it here so it is never
        // mistaken for a product option below.
        delete captured.__stepState;

        // Mark sold-out sizes BEFORE restoring the customer's previous size, so
        // the `!radio.disabled` guard below refuses a size that is sold out on
        // the product we just swapped to and Shopify's own pick stands instead.
        applySizeAvailability(section);

        const container = section.querySelector('variant-radios, variant-selects');
        if (container && Object.keys(captured).length > 0) {
            let changed = false;
            for (const [name, value] of Object.entries(captured)) {
                const radio = container.querySelector(
                    `input[type="radio"][name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
                );
                if (radio && !radio.disabled && !radio.checked) {
                    radio.checked = true;
                    changed = true;
                    continue;
                }
                const select = container.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
                if (select && Array.from(select.options).some((o) => o.value === value) && select.value !== value) {
                    select.value = value;
                    changed = true;
                }
            }
            if (changed) {
                container.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        // Restore the inlay selection after the size options so the inlay's
        // size-matched variant resolution sees the final bag variant. The custom
        // element may not be upgraded yet (its script re-executes async after the
        // swap) — park the state on the dataset for connectedCallback in that case.
        if (inlayState) {
            const configurator = section.querySelector('inlay-configurator');
            if (configurator) {
                if (typeof configurator.restoreState === 'function') {
                    configurator.restoreState(inlayState);
                } else {
                    configurator.dataset.pendingState = JSON.stringify(inlayState);
                }
            }
        }
    }

    showMediaSkeleton() {
        const mediaWrapper = document.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;
        mediaWrapper.classList.add('skeleton-loading');
        const placeholder = document.createElement('div');
        placeholder.className = 'skeleton-media-placeholder';
        mediaWrapper.prepend(placeholder);
    }

    removeMediaSkeleton() {
        document.querySelectorAll('.skeleton-media-placeholder').forEach(el => el.remove());
        const mediaWrapper = document.querySelector('.product__media-wrapper');
        if (mediaWrapper) mediaWrapper.classList.remove('skeleton-loading');
    }

    async swapProductSection(product, cacheKey, preservedOptions) {
        this.showMediaSkeleton();
        try {
            let pageHtml;
            if (this.prefetchCache && this.prefetchCache[cacheKey]) {
                pageHtml = await this.prefetchCache[cacheKey];
            }
            if (!pageHtml) {
                const res = await fetch(product.url);
                pageHtml = await res.text();
            }
            if (!pageHtml) {
                window.location.href = product.url;
                return;
            }

            const parser = new DOMParser();
            const newDoc = parser.parseFromString(pageHtml, 'text/html');

            // Find the main product section in both current and fetched page
            const currentSection = document.getElementById('shopify-section-main')
                || document.querySelector('section[id^="MainProduct-"]');
            const newSection = newDoc.getElementById('shopify-section-main')
                || newDoc.querySelector('section[id^="MainProduct-"]');

            if (!currentSection || !newSection) {
                console.error('Section swap - could not find sections', { current: !!currentSection, new: !!newSection });
                window.location.href = product.url;
                return;
            }

            // Hand the funnel state to the incoming picker before it is upgraded,
            // so it renders the partially confirmed state directly — no flash of a
            // fully answered funnel, and no cross-swap races over a shared global.
            const stepState = preservedOptions && preservedOptions.__stepState;
            if (stepState) {
                const newPicker = newSection.querySelector('linked-variant-picker-multi');
                if (newPicker) newPicker.setAttribute('data-pending-step-state', JSON.stringify(stepState));
            }

            currentSection.replaceWith(newSection);
            history.pushState({}, '', product.url);
            document.title = newDoc.title || document.title;

            // Re-run scripts in the new section
            currentSection.parentElement; // newSection is now in DOM
            const liveSection = document.getElementById(newSection.id);
            liveSection?.querySelectorAll('script').forEach(oldScript => {
                const type = (oldScript.getAttribute('type') || '').toLowerCase();
                // Only re-execute real JavaScript. Data blocks (e.g.
                // type="application/json" mapping/price scripts) are read via
                // querySelector, never executed — re-running them as JS throws
                // "unexpected token ':'" on the JSON.
                const isExecutable = type === '' || type === 'text/javascript' || type === 'application/javascript' || type === 'module';
                if (!isExecutable) return;
                const newScript = document.createElement('script');
                for (const { name, value } of oldScript.attributes) newScript.setAttribute(name, value);
                if (!oldScript.src) newScript.textContent = oldScript.textContent;
                oldScript.replaceWith(newScript);
            });

            this.applyPreservedOptions(liveSection, preservedOptions);

            // Re-initialise the Essential Preorder app-embed (the "Notify me when
            // available" / preorder widget) for the product we just swapped to. The
            // widget lives OUTSIDE the swapped section (it's injected globally at
            // content_for 'theme'), so replaceWith() never touches it and it would
            // otherwise keep showing the previous product's stock state until a full
            // page reload. newDoc is the full page we already fetched, so it holds a
            // correctly server-rendered widget for the new product to harvest from.
            this.refreshPreorderWidget(newDoc, product, preservedOptions);

            if (window.Shopify && window.Shopify.PaymentButton) {
                window.Shopify.PaymentButton.init();
            }
        } catch (e) {
            console.error('Section swap failed, falling back to navigation', e);
            this.removeMediaSkeleton();
            window.location.href = product.url;
        }
    }

    handleProductNotFound(combination) {
        console.warn(`No product found for combination: ${combination.inside}-${combination.outside}`);
        console.log('Available combinations in mapping:', Object.keys(this.productMapping));

        this.dispatchEvent(new CustomEvent('variantChanged', {
            detail: { combination, available: false }
        }));
    }

    refreshPreorderWidget(newDoc, product, preservedOptions) {
        // Copies the freshly server-rendered Essential Preorder widget state onto
        // the live widget so it re-renders for the swapped product.
        //
        // The widget keeps the *selected variant id* in internal (Preact) state
        // that survives re-renders. If we only swap the `product` attribute, that
        // state still points at the PREVIOUS product's variant id — which isn't in
        // the new product's variant list — so the widget can't find a matching
        // variant and wrongly shows "Notify me" on an in-stock product. The widget
        // does self-heal by reading the selected variant from the DOM
        // (input[name="id"]) / the ?variant= URL param, but that's async and races
        // Dawn's own variant update, which is why the bug was intermittent.
        //
        // So we resolve the correct new-product variant ourselves and pin it in the
        // channels the widget reads (config global + ?variant= URL param) BEFORE
        // triggering the re-render, then re-assert once after the DOM settles.
        try {
            const liveEmbed = document.querySelector('essential-preorder-app-embed');
            const freshEmbed = newDoc.querySelector('essential-preorder-app-embed');
            // App disabled, not on this page, or theme without the embed — nothing
            // to refresh, so bail quietly rather than throwing mid-swap.
            if (!liveEmbed || !freshEmbed) return;

            const productAttr = freshEmbed.getAttribute('product');
            let newProduct = null;
            try { newProduct = JSON.parse(productAttr); } catch (e) { /* leave null */ }

            // Which variant should be considered selected on the new product?
            // Honour the size/option the customer had picked (preservedOptions);
            // fall back to the first available variant, then the first variant.
            const variantId = this.resolveSelectedVariantId(newProduct, preservedOptions);

            // Push the new page's global config first (market, locale, ...), THEN
            // override the selected-variant field with our resolved id so it isn't
            // left on the new product's default when the customer kept a size.
            this.syncPreorderConfig(newDoc);
            if (variantId != null && window.essentialPreorderConfigs) {
                window.essentialPreorderConfigs.selectedOrFirstAvailableVariantId = String(variantId);
            }

            // Mirror the selection into the URL. The widget resolves the selected
            // variant from ?variant= (the `d` branch of its effect), and this also
            // matches what a real page load / Dawn variant change would leave.
            if (variantId != null) {
                try {
                    const url = new URL(window.location.href);
                    url.searchParams.set('variant', String(variantId));
                    history.replaceState(history.state, '', url.toString());
                } catch (e) { /* non-fatal */ }
            }

            // Push the new product's data into the widget. It's a
            // preact-custom-element: its reactive props are the camelCase names in
            // observedAttributes (inventoryQuantityByVariantId, product, ...), and
            // they only update when its attributeChangedCallback runs. The browser
            // fires that callback ONLY for attribute names that match the (camelCase)
            // observed list — which the kebab-case DOM attributes never do, except
            // `product`. So a plain setAttribute('inventory-quantity-by-variant-id')
            // is a silent no-op and leaves the inventory map frozen at its mount
            // value; that stale map is missing the swapped product's variant ids, so
            // the back-in-stock check (inventoryQuantityByVariantId[selected] > 0 ?
            // hide : show) wrongly shows the button on in-stock variants.
            //
            // We therefore invoke attributeChangedCallback ourselves for every
            // attribute. The library converts the kebab name to camelCase internally
            // (Te()), so this updates the real prop and re-renders. We also mirror
            // the value into the DOM attribute so the markup stays truthful (and a
            // future remount reads correct data). `product` is applied LAST so the
            // final re-render sees every freshly-updated prop.
            const pushAttr = (name, value) => {
                try { liveEmbed.attributeChangedCallback(name, null, value); } catch (e) { /* not mounted yet */ }
                liveEmbed.setAttribute(name, value);
            };

            for (const { name, value } of Array.from(freshEmbed.attributes)) {
                if (name === 'product') continue;
                pushAttr(name, value);
            }

            const applyProduct = () => {
                if (productAttr != null) pushAttr('product', productAttr);
                // Gate on the swapped combo's inlay stock. Runs AFTER the fresh
                // inventory map is pushed above, so it can zero the selected bag
                // variant (forcing "Notify me") when the matching inlay is sold
                // out even though the bag itself is available. Reads the live
                // (already swapped-in) gate; we only pass the resolved variant id.
                applyInlayGate(variantId);
            };
            applyProduct();

            // Re-assert on the next frame. By then Dawn's async variant/section
            // update has settled, so the widget's DOM-reading effect lands on the
            // final selected variant — closing the race that made the button
            // flicker in on available products.
            requestAnimationFrame(applyProduct);
        } catch (e) {
            // Never let a widget-refresh problem break the product swap itself.
            console.error('Preorder widget refresh failed', e);
        }
    }

    resolveSelectedVariantId(product, preservedOptions) {
        // Returns the variant id on `product` that matches the customer's preserved
        // option selection (e.g. size "XS"). Falls back to the first available
        // variant, then the first variant. Returns null if there's nothing to go on.
        const variants = (product && product.variants) || [];
        if (!variants.length) return null;

        const opts = Object.assign({}, preservedOptions);
        delete opts.__inlayState; // inlay configurator state, not a product option
        const names = product.options || []; // e.g. ["Größe"]
        const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

        if (names.length && Object.keys(opts).length) {
            const match = variants.find((v) =>
                names.every((name, idx) => {
                    // Ignore options the customer hadn't selected/that don't carry over.
                    if (!(name in opts)) return true;
                    return norm(v['option' + (idx + 1)]) === norm(opts[name]);
                })
            );
            if (match) return match.id;
        }

        const firstAvailable = variants.find((v) => v.available);
        return (firstAvailable || variants[0]).id;
    }

    syncPreorderConfig(newDoc) {
        const configScript = Array.from(newDoc.querySelectorAll('script:not([src])'))
            .find((s) => s.textContent.includes('essentialPreorderConfigs'));
        if (!configScript) return;
        const runner = document.createElement('script');
        runner.textContent = configScript.textContent;
        document.head.appendChild(runner);
        runner.remove();
    }
}
customElements.define('linked-variant-picker-multi', LinkedVariantPickerMulti);

/* --- Inlay stock gate -------------------------------------------------------
   Weekender bags are combined products; their matching inlay (custom.fur_color +
   custom.outer_color) is a SEPARATE product with its own inventory, so the bag
   must not be sellable when that inlay is out of stock for the selected size.

   snippets/inlay-configurator.liquid emits { bagVariantId: inlayInStock } as
   #inlay-availability-<section>. When the selected bag variant's inlay is sold
   out we (a) add .inlay-oos to .product-form__buttons so assets/base.css hides
   the buy buttons, and (b) zero that variant in the Essential widget's inventory
   map so its "Notify me when available" button renders. If Essential has no
   campaign for the variant the button won't appear, but .inlay-oos still hides
   the buy buttons and a CSS fallback note keeps the bag unbuyable. */

function pushEmbedAttr(embed, name, value) {
    // Same mechanism as refreshPreorderWidget: the preact-custom-element only
    // reacts when attributeChangedCallback runs, and the browser never fires it
    // for kebab-case attributes — so invoke it ourselves, then mirror to the DOM.
    try { embed.attributeChangedCallback(name, null, value); } catch (e) { /* not mounted yet */ }
    embed.setAttribute(name, value);
}

function readInlayAvailabilityMap(doc, sectionId) {
    const el = doc.getElementById('inlay-availability-' + sectionId);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
}

function applyInlayGate(variantId) {
    // Self-contained: always reads the LIVE gate. After an AJAX product swap the
    // whole section is replaced (currentSection.replaceWith(newSection)) BEFORE
    // refreshPreorderWidget runs, so the live gate is already the swapped-in
    // product's — with its own data-section (these JSON-template section ids are
    // product-specific, so we must NOT trust the old picker's this.sectionId).
    // Pass variantId when the caller already resolved it (swap); otherwise it's
    // read from the live variant input.
    try {
        const gate = document.querySelector('inlay-stock-gate[data-section]');
        if (!gate) return; // Not a Weekender with an inlay — nothing to gate.
        const sectionId = gate.dataset.section;

        // Toggle the state on the whole product-info wrapper so one ancestor
        // class drives every rule in assets/base.css: hide the buy buttons,
        // quantity selector and pickup availability, and reveal the "Bald wieder
        // verfügbar" badge next to the price.
        const wrapper = gate.closest('[id^="MainProduct-"]') || gate.closest('.shopify-section');
        if (!wrapper) return;

        // The variant input lives inside the product <form id="product-form-…">;
        // the form id differs across templates, so fall back to the wrapper.
        const form = gate.dataset.productFormId ? document.getElementById(gate.dataset.productFormId) : null;
        const scope = form || wrapper;
        let vid = variantId;
        if (vid == null) {
            const input = scope.querySelector('input[name="id"], select[name="id"]');
            vid = input ? input.value : null;
        }

        const map = readInlayAvailabilityMap(document, sectionId);
        // Fail OPEN: only a positively-known sold-out inlay gates the bag. A missing
        // map, an unparseable one, or a variant absent from it all leave the product
        // buyable, so a gap in the projection can never take the storefront down.
        const oos = !!(map && vid != null && map[String(vid)] === false);

        wrapper.classList.toggle('inlay-oos', oos);
        if (!oos) return; // In stock (or unknown) → leave the real inventory intact.

        const embed = document.querySelector('essential-preorder-app-embed');
        if (!embed) return;
        let inv = {};
        try { inv = JSON.parse(embed.getAttribute('inventory-quantity-by-variant-id') || '{}'); } catch (e) { inv = {}; }
        if (inv[String(vid)] === 0) return; // already forced OOS
        inv[String(vid)] = 0;
        pushEmbedAttr(embed, 'inventory-quantity-by-variant-id', JSON.stringify(inv));
        // Re-render so the back-in-stock decision re-runs against the zeroed qty.
        const productAttr = embed.getAttribute('product');
        if (productAttr != null) pushEmbedAttr(embed, 'product', productAttr);
    } catch (e) {
        console.error('Inlay stock gate failed', e);
    }
}

function initInlayStockGate() {
    // .inlay-oos hides the buy buttons synchronously; the rAF/timeout re-runs let
    // the notify-me push land once the Essential embed has mounted (its mount
    // time isn't observable from here).
    const applyBurst = () => { applyInlayGate(); requestAnimationFrame(() => applyInlayGate()); };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyBurst);
    } else {
        applyBurst();
    }
    setTimeout(() => applyInlayGate(), 600);
    setTimeout(() => applyInlayGate(), 1600);
    // Bag size change (Dawn dispatches a bubbling change on input[name="id"], the
    // same signal inlay-configurator.js listens for) swaps the matching inlay
    // variant, so re-evaluate the gate.
    document.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'id') applyBurst();
    });
}
initInlayStockGate();

function initStepwiseSizeSync() {
    // A size change can change which OTHER options are still reachable on a
    // multi-option product, so re-evaluate the greying after every pick. Only
    // relevant while a stepwise picker is on the page.
    document.addEventListener('change', (e) => {
        const container = e.target && e.target.closest && e.target.closest('variant-radios');
        if (!container) return;
        const scope = container.closest('[id^="MainProduct-"]') || container.closest('.shopify-section');
        if (!scope || !scope.querySelector('linked-variant-picker-multi[data-stepwise="true"]')) return;
        applySizeAvailability(scope);
    });
}
initStepwiseSizeSync();
}
